import { useEffect, useRef, useState } from "react";
import { getDaysInMonth, parseISO, startOfDay, startOfMonth } from "date-fns";
import { numArrayOfLength } from "./util";
import { DateRangeInput } from "src/graphql/types";

const rangeDate = (date: Date | string) =>
  startOfDay(typeof date === "string" ? parseISO(date) : date);

export const useRangePickerState = (
  onChange: (range: DateRangeInput) => void,
  value?: DateRangeInput,
  controlled = value !== undefined,
) => {
  const [internalValue, setInternalValue] = useState<DateRangeInput>();
  const applied = controlled ? value : internalValue;
  const appliedStart = applied?.startDate
    ? rangeDate(applied.startDate)
    : undefined;
  const appliedEnd = applied?.endDate ? rangeDate(applied.endDate) : undefined;
  const initialMonth = appliedStart ?? startOfDay(new Date());
  const [draftStart, setDraftStart] = useState<Date>();
  const [displayedMonth, setDisplayedMonth] = useState(
    startOfMonth(initialMonth),
  );
  const startKey = appliedStart?.getTime();
  const endKey = appliedEnd?.getTime();
  const emittedRange = useRef<{ start: number; end: number }>();

  useEffect(() => {
    setDraftStart(undefined);
    const localEcho =
      !!emittedRange.current &&
      emittedRange.current?.start === startKey &&
      emittedRange.current?.end === endKey;
    if (!localEcho) {
      setDisplayedMonth(startOfMonth(appliedStart ?? new Date()));
    }
  }, [startKey, endKey]);

  // Consume the marker on the next render even if the parent ignores the range.
  // A later external value must still recenter the calendar.
  useEffect(() => {
    emittedRange.current = undefined;
  });

  const showMonth = (date: Date) => setDisplayedMonth(startOfMonth(date));
  const nextMonth = () =>
    showMonth(
      new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 1),
    );
  const previousMonth = () =>
    showMonth(
      new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() - 1, 1),
    );
  const setDate = (day: number) => {
    const date = new Date(
      displayedMonth.getFullYear(),
      displayedMonth.getMonth(),
      day,
    );
    if (!draftStart || date < draftStart) {
      setDraftStart(date);
      return;
    }
    const range = { startDate: draftStart, endDate: date };
    emittedRange.current = {
      start: draftStart.getTime(),
      end: date.getTime(),
    };
    setDraftStart(undefined);
    if (!controlled) setInternalValue(range);
    onChange(range);
  };

  const daysInMonth = numArrayOfLength(getDaysInMonth(displayedMonth));
  const emptyDays = numArrayOfLength(displayedMonth.getDay());

  return {
    nextMonth,
    previousMonth,
    showMonth,
    setDate,
    daysOfMonth: [...emptyDays.map(() => undefined), ...daysInMonth],
    displayedMonth,
    startDate: draftStart ?? appliedStart,
    endDate: draftStart ? undefined : appliedEnd,
    isDraft: !!draftStart,
    applied,
  };
};
